import { Badge, BadgeWithIcon, Button, Dropdown } from "@its-engineering-and-development/ui-foundation/components";
import { cx } from "@its-engineering-and-development/ui-foundation/utils";
import { Fragment } from "react";
import { PERMISSION_ACCESS, isPermissionAccess, type PermissionAccess } from "@app/types/permissions";

const ACCESS_LABELS: Record<PermissionAccess, string> = {
    [PERMISSION_ACCESS.NoAccess]: "No access",
    [PERMISSION_ACCESS.ReadOnly]: "Read only",
    [PERMISSION_ACCESS.ReadWrite]: "Read write",
};

const ACCESS_OPTIONS: PermissionAccess[] = [PERMISSION_ACCESS.NoAccess, PERMISSION_ACCESS.ReadOnly, PERMISSION_ACCESS.ReadWrite];

/** Fixed hex values for read-only pills; Badge theme tokens do not expose these exact gray shades. */
export const FIXED_PERMISSION_PILL_CLASS = "whitespace-nowrap !bg-[#f1f5f9] !text-gray-900 !ring-0 font-normal";

/** Badge pill-color applies bg-utility-brand-50; override with arbitrary hex — opacity utilities do not win here. */
export const CONFIGURABLE_PERMISSION_PILL_CLASS = "cursor-pointer whitespace-nowrap !bg-[#f0f7ff] !text-brand-500 !ring-brand-100";

const PERMISSION_PILL_TRIGGER_BUTTON_CLASS = cx(
    "!h-auto !min-h-0 !justify-center !p-0 !shadow-none !ring-0 !outline-none before:!hidden",
    "hover:!bg-transparent data-pressed:!bg-transparent",
    "focus:!outline-none focus-visible:!outline-none focus-visible:!outline-offset-0",
    "hover:*:data-text:!no-underline *:data-text:!no-underline *:data-text:!decoration-transparent",
);

function LockIcon({ className }: { className?: string }) {
    return (
        <svg aria-hidden viewBox="0 0 16 16" fill="none" className={className}>
            <path d="M5.5 6.5V5a2.5 2.5 0 015 0v1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <rect x="3" y="6.5" width="10" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    );
}

function ConfigureIcon({ className }: { className?: string }) {
    return (
        <svg aria-hidden viewBox="0 0 16 16" fill="none" className={className}>
            <path d="M2 4h12M4 8h8M6 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    );
}

function createPermissionAccessRadioIcon(selected: boolean) {
    return function PermissionAccessRadioIcon({ className }: { className?: string }) {
        return (
            <span
                aria-hidden
                className={cx(
                    "!mr-1.5 !size-3 !min-h-3 !min-w-3 shrink-0 rounded-full border",
                    selected ? "border-transparent bg-utility-brand-500" : "border-gray-300 bg-white",
                    className,
                )}
            />
        );
    };
}

function PermissionAccessDropdown({ access, onChange }: { access: PermissionAccess; onChange: (access: PermissionAccess) => void }) {
    return (
        <Dropdown.Root>
            <Button
                aria-label={`Permission: ${ACCESS_LABELS[access]}`}
                color="tertiary"
                size="sm"
                noTextPadding
                className={PERMISSION_PILL_TRIGGER_BUTTON_CLASS}
            >
                <BadgeWithIcon type="pill-color" color="brand" size="sm" iconTrailing={ConfigureIcon} className={CONFIGURABLE_PERMISSION_PILL_CLASS}>
                    {ACCESS_LABELS[access]}
                </BadgeWithIcon>
            </Button>

            <Dropdown.Popover className="w-40 p-1">
                <Dropdown.Menu
                    aria-label="Permission options"
                    selectionMode="none"
                    disallowEmptySelection={false}
                    onAction={(key) => {
                        const value = String(key);
                        if (isPermissionAccess(value)) onChange(value);
                    }}
                >
                    {ACCESS_OPTIONS.map((option, index) => {
                        const selected = option === access;

                        return (
                            <Fragment key={option}>
                                {index > 0 ? <Dropdown.Separator /> : null}
                                <Dropdown.Item
                                    id={option}
                                    textValue={ACCESS_LABELS[option]}
                                    label={ACCESS_LABELS[option]}
                                    icon={createPermissionAccessRadioIcon(selected)}
                                    className={cx(
                                        "[&>div]:py-1.5 [&>div]:!outline-none [&>div]:focus-visible:!outline-none [&_span]:text-xs [&_span]:font-normal",
                                        selected ? "[&>div]:bg-utility-brand-50 [&_span]:text-utility-brand-700" : "[&_span]:text-gray-700",
                                    )}
                                />
                            </Fragment>
                        );
                    })}
                </Dropdown.Menu>
            </Dropdown.Popover>
        </Dropdown.Root>
    );
}

export function getPermissionAccessLabel(access: PermissionAccess) {
    return ACCESS_LABELS[access];
}

export function PermissionPill({
    access,
    editable,
    canEdit = true,
    onChange,
}: {
    access: PermissionAccess | null;
    editable: boolean;
    canEdit?: boolean;
    onChange?: (access: PermissionAccess) => void;
}) {
    if (!access) {
        return null;
    }

    if (!editable || !canEdit) {
        if (access === PERMISSION_ACCESS.NoAccess) {
            if (!editable) return <span className="text-sm text-gray-400">No access</span>;
            return (
                <Badge type="pill-color" color="gray" size="sm">
                    {ACCESS_LABELS[access]}
                </Badge>
            );
        }
        return (
            <BadgeWithIcon type="pill-color" color="gray" size="sm" iconTrailing={LockIcon} className={FIXED_PERMISSION_PILL_CLASS}>
                {ACCESS_LABELS[access]}
            </BadgeWithIcon>
        );
    }

    return <PermissionAccessDropdown access={access} onChange={(nextAccess) => onChange?.(nextAccess)} />;
}

export function PermissionLegend() {
    return (
        <div className="flex flex-wrap items-center gap-4 border-t border-gray-200 px-4 py-3 text-xs font-medium text-gray-500">
            <span>Legend:</span>
            <span className="flex items-center gap-1.5">
                <BadgeWithIcon type="pill-color" color="brand" size="sm" iconTrailing={ConfigureIcon} className={CONFIGURABLE_PERMISSION_PILL_CLASS}>
                    Configurable
                </BadgeWithIcon>
            </span>
            <span className="flex items-center gap-1.5">
                <BadgeWithIcon type="pill-color" color="gray" size="sm" iconTrailing={LockIcon} className={FIXED_PERMISSION_PILL_CLASS}>
                    Fixed
                </BadgeWithIcon>
            </span>
        </div>
    );
}
